import { useState } from 'react';

import { css } from '@emotion/css';
import { Dialog, Theme } from '@mui/material';
import { useIsMutating } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Icons } from '@/assets/icons';
import { useStyles } from '@/hooks/useStyles';
import { CarePlan } from '@/models/CarePlanModel';
import { FileModel } from '@/models/FileModel';
import { Queries } from '@/queries/Queries';
import { Button } from '@/uiKit/atoms/Button.tsx';
import { Form } from '@/uiKit/atoms/Form.tsx';
import { CardContent } from '@/uiKit/atoms/card/CardContent.tsx';
import { CardFooter } from '@/uiKit/atoms/card/CardFooter.tsx';
import { CardHeader } from '@/uiKit/atoms/card/CardHeader.tsx';
import { ControlledFileUpload } from '@/uiKit/molecules/ControlledFileInput';
import { ControlledTextField } from '@/uiKit/molecules/ControlledTextField';

export const EDIT_THERAPEUTIC_OBJECTIVES_FORM_ID =
  'EditTheurapeuticObjectivesForm';

type TherapeuticObjectivesModalProps = {
  carePlan: CarePlan;
  onClose: () => void;
};

type TherapeuticObjectivesFormData = {
  description?: string;
  descriptionAttachment?: FileModel;
};

const TherapeuticObjectivesModal = ({
  carePlan,
  onClose,
}: TherapeuticObjectivesModalProps) => {
  const editObjectives = Queries.practitioner.useEditTherapeuticObjectives();
  const form = useForm<TherapeuticObjectivesFormData>({
    defaultValues: {
      description: carePlan.description,
      descriptionAttachment: carePlan.descriptionAttachment,
    },
  });

  const isMutating = useIsMutating();

  const styles = useStyles(makeStyles);
  const { t } = useTranslation();

  const patient = Queries.practitioner.usePatient(carePlan.subject);

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
      <CardHeader
        title={t('diabetesForms.goalsShort')}
        variant="contained"
        onExit={onClose}
      />

      <CardContent className={styles.content}>
        <Form
          onSubmit={data => {
            return editObjectives.mutate(
              {
                carePlanId: carePlan.id,
                data: data,
              },
              {
                onSuccess: onClose,
              },
            );
          }}
          id={EDIT_THERAPEUTIC_OBJECTIVES_FORM_ID}
          formContext={form}
        >
          <div className={styles.formContainer}>
            <ControlledTextField
              name="description"
              multiline
              label={t('diabetesForms.goalsShort')}
            />
            <ControlledFileUpload
              name="descriptionAttachment"
              fileType="description"
              patientInfo={patient}
              defaultValue={carePlan.descriptionAttachment}
              hasTrash={true}
            />
          </div>
        </Form>
      </CardContent>
      <CardFooter>
        <Button variant="outlined" onClick={onClose}>
          {t('button.cancel')}
        </Button>
        <Button
          variant="contained"
          type="submit"
          form={EDIT_THERAPEUTIC_OBJECTIVES_FORM_ID}
          disabled={isMutating > 0}
          pending={editObjectives.isPending}
        >
          {t('button.next')}
        </Button>
      </CardFooter>
    </Dialog>
  );
};

type EditTherapeuticObjectivesProps = {
  carePlan: CarePlan;
};

export const EditTherapeuticObjectives = ({
  carePlan,
}: EditTherapeuticObjectivesProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <Button
        onClick={() => setIsModalOpen(true)}
        variant="text"
        startIcon={<Icons.edit height="0.75em" width="0.75em" color="black" />}
      />
      {isModalOpen ? (
        <TherapeuticObjectivesModal
          onClose={() => setIsModalOpen(false)}
          carePlan={carePlan}
        />
      ) : null}
    </>
  );
};

const makeStyles = (theme: Theme) => ({
  content: css`
    padding: ${theme.spacing(20)};
    justify-content: center;
    gap: ${theme.spacing(12)};
  `,
  footer: css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding-left: ${theme.spacing(12)};
    padding-right: ${theme.spacing(12)};
    margin-top: ${theme.spacing(12)};
  `,
  formContainer: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: ${theme.spacing(12)};
  `,
});
